import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useLocation } from 'react-router-dom';
import { homePathForRole } from './roleHome';

// Liste aller Routen die Authentifizierung erfordern
const protectedRoutes = [
    // Student/Teacher spezifische Routen
    '/app',
    '/student',
    '/teacher',
    '/admin',
    '/profile',
    '/scheduling',
    '/multi-view',

    // Persönliche Daten Routen
    '/me/available-times',
    '/me/times',
    '/me/classes',

    // Daten-Management Routen
    '/save-class',
    '/load-class',
    '/calculate',
    '/calculate-from-db',

    // Upload/Download Routen
    '/upload-students',
    '/upload-teachers',
    '/download-csv',
    '/upload-csv',

    // Weitere geschützte Routen
    '/protected-endpoint',
    '/api/extract-hmtm-csv',
];

/**
 * Routen, die nur einer Rolle gehören. Wer mit der anderen Rolle hier landet
 * (Bookmark, Tippfehler, Link aus einer Mail), wird auf seine Startseite geleitet
 * statt eine leere Seite oder einen 403 vom Backend zu sehen.
 * Admins (auch impersonierend) sind ausgenommen.
 */
const roleRoutes: Array<{ prefix: string; role: 'teacher' | 'student' }> = [
    { prefix: '/scheduling', role: 'teacher' },
    { prefix: '/me/classes/teacher', role: 'teacher' },
    { prefix: '/app', role: 'teacher' },
    { prefix: '/student', role: 'student' },
];

const matchesPrefix = (path: string, prefix: string): boolean =>
    path === prefix || path.startsWith(prefix + '/');

// Prüft ob aktuelle Route geschützt ist
export const isProtectedRoute = (path: string): boolean => {
    return protectedRoutes.some(route => path.startsWith(route));
};

const ProtectedRoutes = ({ children }: { children: ReactNode }) => {
  const { isLoggedIn, loading, userRole, isImpersonating } = useAuth();
  const location = useLocation();

  const isProtectedRoute = protectedRoutes.some(route =>
    location.pathname.startsWith(route)
  );

  // I1: Während der initiale Auth-Check noch läuft, nicht voreilig wegredirecten.
  // Sonst wird beim Hard-Reload (F5) auf einer geschützten Route ein eingeloggter
  // User kurz auf "/" umgeleitet, bevor checkAuth() fertig ist (Flash-Redirect).
  if (loading && isProtectedRoute) {
    return null;
  }

  if (!isLoggedIn && isProtectedRoute) {
    // Speichere die ursprünglich angeforderte URL für Redirect nach Login
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (isLoggedIn && !isImpersonating && (userRole === 'teacher' || userRole === 'student')) {
    const wrongRole = roleRoutes.find(
      r => matchesPrefix(location.pathname, r.prefix) && r.role !== userRole
    );
    if (wrongRole) {
      const home = homePathForRole(userRole);
      if (home) {
        return <Navigate to={home} replace />;
      }
    }
  }

  return <>{children}</>;
};

export default ProtectedRoutes;
